import React, { useState, useEffect } from 'react';
import {
  ToolOutlined,
  HomeOutlined,
  TeamOutlined,
  PlusOutlined,
  DownOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { message } from 'antd';
import axios from 'axios';
import { useAuth } from '../../../../../contexts/AuthContext';

const serviceFilters = [
  { id: 'service_type', label: 'Service Type' },
  { id: 'last_service', label: 'Last Service Date' },
  { id: 'service_frequency', label: 'Service Frequency' },
  { id: 'equipment_age', label: 'Equipment Age' },
  { id: 'contract_status', label: 'Contract Status' },
];

const geoFilters = [
  { id: 'city', label: 'City' },
  { id: 'state', label: 'State' },
  { id: 'zip_code', label: 'ZIP Code' },
  { id: 'distance', label: 'Distance from Location' },
  { id: 'customer_type', label: 'Customer Type' },
];

const BuildCustomAudience = ({ campaignData, onUpdate, onNext }) => {
  const { token } = useAuth();
  const [activeServiceFilters, setActiveServiceFilters] = useState([]);
  const [activeGeoFilters, setActiveGeoFilters] = useState([]);
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [showGeoDropdown, setShowGeoDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [baseContacts, setBaseContacts] = useState(0);

  // Fetch customer count from API
  useEffect(() => {
    const fetchCustomerCount = async () => {
      setLoading(true);
      try {
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
        
        // Fetch CRM stats for total count
        const statsResponse = await axios.get('/api/crm/stats', { headers: authHeaders });
        const totalCustomers = statsResponse.data?.totalCustomers || 0;
        setBaseContacts(totalCustomers);
      } catch (error) {
        console.error('Error fetching customer count:', error);
        message.error('Failed to load customer data');
        setBaseContacts(0);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerCount();
  }, [token]);
  
  // Calculate estimated contacts based on filters and real customer data
  const estimatedContacts = (() => {
    if (activeServiceFilters.length === 0 && activeGeoFilters.length === 0) {
      return baseContacts;
    }
    
    // Start with base count
    let count = baseContacts;
    
    // Service-based filters (estimate based on filter count)
    if (activeServiceFilters.length > 0) {
      // Each service filter reduces audience by ~15-20%
      const reductionFactor = Math.pow(0.82, activeServiceFilters.length);
      count = Math.floor(count * reductionFactor);
    }
    
    // Geographic filters (estimate based on filter count)
    if (activeGeoFilters.length > 0) {
      // Each geo filter reduces audience by ~10-15%
      const reductionFactor = Math.pow(0.87, activeGeoFilters.length);
      count = Math.floor(count * reductionFactor);
    }
    
    return Math.max(10, count);
  })();

  const totalFilters = activeServiceFilters.length + activeGeoFilters.length;

  const addServiceFilter = (filterId) => {
    if (!activeServiceFilters.includes(filterId)) {
      const newFilters = [...activeServiceFilters, filterId];
      setActiveServiceFilters(newFilters);
      onUpdate({ customFilters: { service: newFilters, geo: activeGeoFilters } });
    }
    setShowServiceDropdown(false);
  };

  const addGeoFilter = (filterId) => {
    if (!activeGeoFilters.includes(filterId)) {
      const newFilters = [...activeGeoFilters, filterId];
      setActiveGeoFilters(newFilters);
      onUpdate({ customFilters: { service: activeServiceFilters, geo: newFilters } });
    }
    setShowGeoDropdown(false);
  };

  const removeFilter = (type, filterId) => {
    if (type === 'service') {
      const newFilters = activeServiceFilters.filter(f => f !== filterId);
      setActiveServiceFilters(newFilters);
      onUpdate({ customFilters: { service: newFilters, geo: activeGeoFilters } });
    } else {
      const newFilters = activeGeoFilters.filter(f => f !== filterId);
      setActiveGeoFilters(newFilters);
      onUpdate({ customFilters: { service: activeServiceFilters, geo: newFilters } });
    }
  };

  const handleContinue = () => {
    onNext();
  };

  // Show loading state
  if (loading) {
    return (
      <div className="step-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <LoadingOutlined style={{ fontSize: 48, color: 'var(--ink)', marginBottom: 16 }} spin />
          <p style={{ color: '#8c8c8c' }}>Loading customer data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="step-container">
      <h1 className="step-title">Build Your Custom Audience</h1>
      <p className="step-subtitle">Add filters to target specific customer segments from your CRM</p>

      <div className="custom-audience-container">
        {/* Left: Filters */}
        <div className="filters-section">
          {/* Service-Based Filters */}
          <div className="filter-group">
            <div className="filter-group-header">
              <ToolOutlined />
              <span>Service-Based Filters</span>
            </div>
            
            {activeServiceFilters.map(filterId => {
              const filter = serviceFilters.find(f => f.id === filterId);
              return (
                <div key={filterId} className="active-filter">
                  <span>{filter?.label}</span>
                  <button onClick={() => removeFilter('service', filterId)}>×</button>
                </div>
              );
            })}

            <div className="add-filter-wrapper">
              <button 
                className="add-filter-btn"
                onClick={() => setShowServiceDropdown(!showServiceDropdown)}
              >
                <PlusOutlined /> Add Filter <DownOutlined />
              </button>
              {showServiceDropdown && (
                <div className="filter-dropdown">
                  {serviceFilters
                    .filter(f => !activeServiceFilters.includes(f.id))
                    .map(filter => (
                      <div 
                        key={filter.id} 
                        className="filter-option"
                        onClick={() => addServiceFilter(filter.id)}
                      >
                        {filter.label}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* Geographic & Demographic Filters */}
          <div className="filter-group">
            <div className="filter-group-header">
              <HomeOutlined />
              <span>Geographic & Demographic Filters</span>
            </div>

            {activeGeoFilters.map(filterId => {
              const filter = geoFilters.find(f => f.id === filterId);
              return (
                <div key={filterId} className="active-filter">
                  <span>{filter?.label}</span>
                  <button onClick={() => removeFilter('geo', filterId)}>×</button>
                </div>
              );
            })}

            <div className="add-filter-wrapper">
              <button 
                className="add-filter-btn"
                onClick={() => setShowGeoDropdown(!showGeoDropdown)}
              >
                <PlusOutlined /> Add Filter <DownOutlined />
              </button>
              {showGeoDropdown && (
                <div className="filter-dropdown">
                  {geoFilters
                    .filter(f => !activeGeoFilters.includes(f.id))
                    .map(filter => (
                      <div 
                        key={filter.id} 
                        className="filter-option"
                        onClick={() => addGeoFilter(filter.id)}
                      >
                        {filter.label}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Audience Preview */}
        <div className="audience-preview-section">
          <div className="preview-header">
            <TeamOutlined />
            <span>Audience Preview</span>
          </div>
          
          <div className="preview-count">
            {estimatedContacts.toLocaleString()}
          </div>
          <div className="preview-label">Estimated Contacts</div>

          <div className="active-filters-info">
            <div className="filters-count">ACTIVE FILTERS ({totalFilters})</div>
            <div className="filters-status">
              {totalFilters === 0 
                ? 'No filters applied - targeting all contacts'
                : `${totalFilters} filter${totalFilters > 1 ? 's' : ''} applied`
              }
            </div>
          </div>

          <div className="preview-tip">
            <strong>Tip:</strong> Start broad and add filters to narrow your audience. More filters = smaller, more targeted audience.
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="campaign-footer">
        <div className="footer-info">
          Add filters to refine audience
        </div>
        <button 
          className="continue-btn" 
          onClick={handleContinue}
        >
          Continue
          <DownOutlined />
        </button>
      </div>
    </div>
  );
};

export default BuildCustomAudience;
